import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Icons, Select, Spin, Typography } from '@pankod/refine-antd'
import { useCustom } from '@pankod/refine-core'
import { useDebounce } from 'usehooks-ts'

import {
  TCertificatePreviewMeta,
  TCertificatePreviewParticipant,
} from '../types'
import { TCommonResponse } from 'src/interfaces/common'

const DEFAULT_VALUE = '__default__'
const PAGE_SIZE = 10

type TParticipantPreviewSelectProps = {
  submissionId: string
  selectedParticipant?: TCertificatePreviewParticipant
  onSelect: (participant?: TCertificatePreviewParticipant) => void
}

const deduplicateParticipants = (
  participants: TCertificatePreviewParticipant[],
) => {
  const participantMap = new Map(
    participants.map((participant) => [String(participant.id), participant]),
  )

  return Array.from(participantMap.values())
}

export const ParticipantPreviewSelect: React.FC<
  TParticipantPreviewSelectProps
> = ({ submissionId, selectedParticipant, onSelect }) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [participants, setParticipants] = useState<
    TCertificatePreviewParticipant[]
  >([])
  const debouncedSearch = useDebounce(search, 300)
  const activeSearchRef = useRef(debouncedSearch)

  useEffect(() => {
    activeSearchRef.current = debouncedSearch
    setCurrentPage(1)
    setParticipants([])
  }, [debouncedSearch])

  useEffect(() => {
    if (!open) {
      setSearch('')
      setCurrentPage(1)
      setParticipants([])
    }
  }, [open])

  const requestSearch = debouncedSearch
  const queryResult = useCustom<
    TCommonResponse<TCertificatePreviewParticipant[], TCertificatePreviewMeta>
  >({
    url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/certificate-tte-submissions/${submissionId}/previews`,
    method: 'get',
    dataProviderName: 'lms',
    config: {
      query: {
        participantName: requestSearch || undefined,
        pageSize: PAGE_SIZE,
        page: currentPage,
      },
    },
    queryOptions: {
      enabled: open && !!submissionId,
      keepPreviousData: true,
      onSuccess: (response) => {
        if (requestSearch !== activeSearchRef.current) return

        const responseData = response?.data?.data || []
        setParticipants((currentParticipants) =>
          deduplicateParticipants(
            currentPage === 1
              ? responseData
              : [...currentParticipants, ...responseData],
          ),
        )
      },
    },
  })

  const meta = queryResult.data?.data?.meta
  const hasNextPage = meta
    ? meta.currentPage < meta.totalPages
    : participants.length >= PAGE_SIZE

  const displayedParticipants = useMemo(
    () =>
      deduplicateParticipants([
        ...(selectedParticipant ? [selectedParticipant] : []),
        ...participants,
      ]),
    [participants, selectedParticipant],
  )

  const options = useMemo(
    () => [
      {
        value: DEFAULT_VALUE,
        title: 'Cari nama peserta...',
        label: (
          <div className="py-1">
            <Typography.Text italic strong>
              Template (Default)
            </Typography.Text>
            <Typography.Text type="secondary" className="block">
              Tampilkan placeholder template
            </Typography.Text>
          </div>
        ),
      },
      ...displayedParticipants.map((participant) => ({
        value: String(participant.id),
        title: participant.name,
        label: (
          <div className="py-1">
            <Typography.Text strong>{participant.name}</Typography.Text>
            {participant.nip && (
              <Typography.Text type="secondary" className="block">
                {participant.nip}
              </Typography.Text>
            )}
          </div>
        ),
      })),
    ],
    [displayedParticipants],
  )

  const footer = (() => {
    if (queryResult.isError) {
      return (
        <div className="border-t p-2 text-center">
          <Typography.Text type="danger" className="block mb-2">
            Gagal memuat daftar peserta
          </Typography.Text>
          <Button
            size="small"
            icon={<Icons.ReloadOutlined />}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => queryResult.refetch()}
          >
            Coba lagi
          </Button>
        </div>
      )
    }

    if (queryResult.isFetching) {
      return (
        <div className=" p-2 text-center">
          <Spin size="small" />
        </div>
      )
    }

    if (participants.length === 0) {
      return (
        <div className=" p-2 text-center">
          <Typography.Text type="secondary">
            Tidak ada peserta ditemukan
          </Typography.Text>
        </div>
      )
    }

    return null
  })()

  return (
    <Select
      className="w-full"
      showSearch
      open={open}
      value={
        selectedParticipant ? String(selectedParticipant.id) : DEFAULT_VALUE
      }
      placeholder="Cari nama peserta..."
      optionLabelProp="title"
      filterOption={false}
      searchValue={search}
      options={options}
      onDropdownVisibleChange={setOpen}
      onSearch={setSearch}
      onSelect={(value) => {
        setSearch('')

        if (value === DEFAULT_VALUE) {
          onSelect(undefined)
          return
        }

        onSelect(
          displayedParticipants.find(
            (participant) => String(participant.id) === String(value),
          ),
        )
      }}
      onPopupScroll={(event) => {
        const target = event.target as HTMLDivElement
        const distanceFromBottom =
          target.scrollHeight - target.scrollTop - target.clientHeight

        if (
          distanceFromBottom <= 24 &&
          hasNextPage &&
          !queryResult.isFetching &&
          !queryResult.isError
        ) {
          setCurrentPage((page) => page + 1)
        }
      }}
      dropdownRender={(menu) => (
        <>
          {menu}
          {footer}
        </>
      )}
    />
  )
}
